// API service for communicating with the backend
// In development, use the frontend dev server so requests go through Vite proxy
// In production, use the backend URL directly
import { auth } from "@/services/firebase";

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

class ApiService {
  private baseUrl: string;
  public defaults: {
    headers: {
      common: Record<string, string>;
    };
  };

  constructor() {
    this.baseUrl = API_BASE_URL;
    this.defaults = {
      headers: {
        common: {},
      },
    };
  }

  /**
   * Get a fresh Firebase ID token for the current user.
   * Firebase automatically refreshes expired tokens, so this ensures
   * we always have a valid token for API requests.
   */
  private async getFreshToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    try {
      // getIdToken() automatically refreshes the token if it's expired
      return await user.getIdToken();
    } catch (error) {
      console.error("[API Service] Failed to get fresh token:", error);
      return null;
    }
  }

  async request(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    // Get a fresh token for each request to avoid 401 errors from expired tokens
    const token = await this.getFreshToken();
    const authHeaders: Record<string, string> = {};
    if (token) {
      authHeaders["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...this.defaults.headers.common,
        ...authHeaders, // Fresh token should override any cached token in defaults
        ...options.headers,
      },
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return { data };
  }

  // HTTP method helpers
  async get(endpoint: string, options: RequestInit = {}) {
    return this.request(endpoint, { method: "GET", ...options });
  }

  async post(endpoint: string, data?: any, options: RequestInit = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async put(endpoint: string, data?: any, options: RequestInit = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async delete(endpoint: string, options: RequestInit = {}) {
    return this.request(endpoint, { method: "DELETE", ...options });
  }

  // Auth endpoints
  async signIn(email: string, password: string) {
    return this.request("/api/auth/signin", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  async signOut() {
    return this.request("/api/auth/signout", {
      method: "POST",
    });
  }

  async getSession() {
    return this.request("/api/auth/session");
  }

  // Chat endpoints
  async createChat(messages: any[]) {
    return this.request("/api/chat", {
      method: "POST",
      body: JSON.stringify({ messages }),
    });
  }

  async getChatHistory() {
    return this.request("/api/chat/history");
  }

  async getChatById(id: string) {
    return this.request(`/api/chat/${id}`);
  }

  // Add more API methods as needed
}

export const apiService = new ApiService();
export default apiService; // Default export for compatibility
